import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/contexts/BrandContext/hooks';
import { ampedService, type AmpedProfilePayload } from '@/services/ampedService';
import { useQuery } from '@tanstack/react-query';

const COUNTRY_CODES = [
  { code: '+1', label: 'US/CA (+1)' },
  { code: '+44', label: 'UK (+44)' },
  { code: '+61', label: 'AU (+61)' },
  { code: '+971', label: 'UAE (+971)' },
  { code: '+966', label: 'SA (+966)' },
  { code: '+65', label: 'SG (+65)' },
  { code: '+91', label: 'IN (+91)' },
  { code: '+49', label: 'DE (+49)' },
  { code: '+33', label: 'FR (+33)' },
  { code: '+39', label: 'IT (+39)' },
  { code: '+34', label: 'ES (+34)' },
  { code: '+55', label: 'BR (+55)' },
  { code: '+52', label: 'MX (+52)' },
  { code: '+81', label: 'JP (+81)' },
  { code: '+82', label: 'KR (+82)' },
  { code: '+64', label: 'NZ (+64)' },
  { code: '+27', label: 'ZA (+27)' },
  { code: '+20', label: 'EG (+20)' },
  { code: '+31', label: 'NL (+31)' },
  { code: '+46', label: 'SE (+46)' },
];

type FormData = AmpedProfilePayload & {
  instagramUsername: string;
  audienceTargeting: string;
  keywordsHashtags: string;
  competitorAccounts: string;
};

const DEFAULT_FORM: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneCountryCode: '+1',
  phoneNumber: '',
  instagramUsername: '',
  twoFaEnabled: false,
  acceleratedPageGrowth: false,
  welcomeDmEnabled: false,
  aiTargeting: false,
  audienceTargeting: '',
  keywordsHashtags: '',
  competitorAccounts: '',
};

export default function ReveshareAmpedSignup() {
  const navigate = useNavigate();
  const { brandDetails } = useBrand();
  const [form, setForm] = useState<FormData>(DEFAULT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: existingProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['amped-profile'],
    queryFn: () => ampedService.getProfile(),
  });

  useEffect(() => {
    if (existingProfile) {
      setForm({
        firstName: existingProfile.firstName,
        lastName: existingProfile.lastName,
        email: existingProfile.email,
        phoneCountryCode: existingProfile.phoneCountryCode,
        phoneNumber: existingProfile.phoneNumber,
        instagramUsername: existingProfile.instagramUsername ?? '',
        twoFaEnabled: existingProfile.twoFaEnabled,
        acceleratedPageGrowth: existingProfile.acceleratedPageGrowth,
        welcomeDmEnabled: existingProfile.welcomeDmEnabled,
        aiTargeting: existingProfile.aiTargeting,
        audienceTargeting: existingProfile.audienceTargeting ?? '',
        keywordsHashtags: existingProfile.keywordsHashtags ?? '',
        competitorAccounts: existingProfile.competitorAccounts ?? '',
      });
    } else if (!existingProfile && brandDetails) {
      setForm((prev) => ({
        ...prev,
        firstName: brandDetails.firstName ?? '',
        lastName: brandDetails.lastName ?? '',
        email: brandDetails.email ?? '',
      }));
    }
  }, [existingProfile, brandDetails]);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!form.firstName || !form.lastName || !form.email || !form.phoneNumber) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await ampedService.saveProfile(form);
      navigate('/brand/reveshare-amped/pricing');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const isEditing = !!existingProfile;

  if (isLoadingProfile) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="mb-8">
        <p className="text-xs font-semibold text-brand-600 uppercase tracking-widest mb-2">
          REVESHARE AMPED
        </p>
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Instagram growth,{' '}
          <span className="text-brand-600">built into your store.</span>
        </h1>
        <p className="text-muted-foreground text-sm">
          {isEditing
            ? 'Update your details and preferences below.'
            : 'Tell us about yourself and your goals so our team can set everything up white-glove for you.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              First name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              required
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Last name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              required
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <select
              name="phoneCountryCode"
              value={form.phoneCountryCode}
              onChange={handleChange}
              className="border border-border rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <input
              type="tel"
              name="phoneNumber"
              value={form.phoneNumber}
              onChange={handleChange}
              required
              placeholder="Phone number"
              className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        {/* Instagram */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Instagram username{' '}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            type="text"
            name="instagramUsername"
            value={form.instagramUsername}
            onChange={handleChange}
            placeholder="@yourbrand"
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        {/* 2FA checkbox */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            name="twoFaEnabled"
            checked={form.twoFaEnabled}
            onChange={handleChange}
            className="mt-0.5 h-4 w-4 accent-brand-600"
          />
          <div>
            <span className="text-sm font-medium text-foreground">
              2FA enabled &amp; complex password set
            </span>
            <p className="text-xs text-muted-foreground mt-0.5">
              We highly recommend enabling 2FA and adding a complex password
              before joining Reveshare Amped.
            </p>
          </div>
        </label>

        {/* Feature checkboxes */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">Enable features</p>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="acceleratedPageGrowth"
              checked={form.acceleratedPageGrowth}
              onChange={handleChange}
              className="mt-0.5 h-4 w-4 accent-brand-600"
            />
            <div>
              <span className="text-sm font-medium text-foreground">
                Enable accelerated page growth
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Grow your page with natural actions on stories and story stickers
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="welcomeDmEnabled"
              checked={form.welcomeDmEnabled}
              onChange={handleChange}
              className="mt-0.5 h-4 w-4 accent-brand-600"
            />
            <div>
              <span className="text-sm font-medium text-foreground">
                Enable welcome DM to new friends
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Send DM to new friends — can be used to incentivize sales and
                affiliates
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="aiTargeting"
              checked={form.aiTargeting}
              onChange={handleChange}
              className="mt-0.5 h-4 w-4 accent-brand-600"
            />
            <div>
              <span className="text-sm font-medium text-foreground">
                Use AI targeting to expand my audience
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Use Meta GraphQL-like targeting — similar to Facebook Ads
              </p>
            </div>
          </label>
        </div>

        {/* Audience targeting */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Audience targeting{' '}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea
            name="audienceTargeting"
            value={form.audienceTargeting}
            onChange={handleChange}
            rows={3}
            placeholder="Add cities, areas and countries to target. Insights, keywords, and hashtags."
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
          />
        </div>

        {/* Keywords / hashtags */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Keywords &amp; hashtags{' '}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea
            name="keywordsHashtags"
            value={form.keywordsHashtags}
            onChange={handleChange}
            rows={2}
            placeholder="#yourbrand, #niche, keywords..."
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
          />
        </div>

        {/* Competitor accounts */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">
            Competitor accounts{' '}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea
            name="competitorAccounts"
            value={form.competitorAccounts}
            onChange={handleChange}
            rows={2}
            placeholder="@competitor1, @competitor2..."
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? 'Saving...' : isEditing ? 'Save & continue to pricing' : 'Continue to pricing'}
        </Button>
      </form>
    </div>
  );
}
